import { jsx } from 'react/jsx-runtime';
import { styled } from '@flex-design-system/stitches-react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useState } from 'react';
import { getColor } from '../../utils/colors.mjs';
import { checkbox, checkboxClickable, checkboxFocusVisible, checkboxActiveBackground } from './checkbox.vanilla.mjs';
import { CheckboxIndicator } from './components/indicator/Indicator.mjs';
import { CheckboxLabel } from './components/label/Label.mjs';
import { useCheckboxContext } from './contexts/context.mjs';

const CheckboxImpl = forwardRef(
  ({
    className: classNameFromProps,
    color,
    disabled: disabledFromProps,
    size: sizeFromProps,
    style: styleFromProps,
    checked: checkedFromProps,
    defaultChecked: defaultCheckedFromProps,
    onCheckedChange,
    ...props
  }, forwardedRef) => {
    const [checkboxElement, setCheckboxElement] = useState(null);
    const [checked = false, setChecked] = useControllableState({
      prop: checkedFromProps,
      defaultProp: defaultCheckedFromProps,
      onChange: onCheckedChange
    });
    const { disabled: disabledFromLabelContext, size: sizeFromLabelContext } = useCheckboxContext("Checkbox");
    const disabled = disabledFromProps ?? disabledFromLabelContext;
    const size = sizeFromProps ?? sizeFromLabelContext;
    const composedRef = useComposedRefs(
      forwardedRef,
      (element) => setCheckboxElement(element)
    );
    const className = classNames(
      checkbox({ size }),
      !disabled && checkboxClickable,
      checkboxFocusVisible,
      classNameFromProps
    );
    const styles = Object.assign(
      {},
      assignInlineVars({
        [checkboxActiveBackground]: color !== void 0 ? getColor(color) : null
      }),
      styleFromProps
    );
    const isWithInCheckboxLabel = checkboxElement?.closest('[data-scope="checkbox"][data-part="label"]') !== null;
    return /* @__PURE__ */ jsx(
      StyledCheckboxPrimitiveRoot,
      {
        "data-scope": "checkbox",
        "data-part": "root",
        "data-within-label": isWithInCheckboxLabel,
        disabled,
        className,
        style: styles,
        checked,
        onCheckedChange: setChecked,
        ref: composedRef,
        ...props,
        children: /* @__PURE__ */ jsx(CheckboxIndicator, { checked })
      }
    );
  }
);
const StyledCheckboxPrimitiveRoot = styled(CheckboxPrimitive.Root);
const Checkbox = Object.assign({}, CheckboxImpl, {
  Label: CheckboxLabel
});
Checkbox.displayName = "Checkbox";

export { Checkbox };
//# sourceMappingURL=Checkbox.mjs.map
